import { ForumOutlined, ReadMore } from '@mui/icons-material'
import { Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetDocumentCommentsCount } from '@/service-library/hooks/document-comments'

type DrawerToggleButtonsProps = {
  documentId?: string
  commentsOverlay: OverlayState
  fieldDetailsOverlay: OverlayState
  disabled?: boolean
}

/**
 * The Comments and Field Details panels share the single right-hand drawer
 * slot, so they are mutually exclusive: opening one closes the other.
 *
 * The Comments toggle is gated behind the `document_comments` feature flag.
 */
export default function DrawerToggleButtons({
  documentId,
  commentsOverlay,
  fieldDetailsOverlay,
  disabled = false,
}: DrawerToggleButtonsProps) {
  const featureFlags = useFeatureFlagContext()

  // Fetch failures fall back to `commentsCount` staying `undefined`, which
  // simply keeps the count from rendering — the toggle itself never depends
  // on this query succeeding. Gated on the feature flag too, since the
  // comments toggle isn't even rendered when it's off — no point fetching a
  // count for a button the user can't see.
  const { commentsCount, isPending } = useGetDocumentCommentsCount({
    filters: { document_id: documentId, deleted_at__isnull: 'true' },
    enabled: !!documentId && featureFlags.document_comments,
    placeholderData: (previousData) => previousData, // Keep the last known count while loading
  })

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {featureFlags.document_comments && (
        <>
          <Tooltip
            title={disabled ? '' : 'Comments'}
            arrow
            disableInteractive
            enterDelay={1000}
          >
            <IconButton
              size="small"
              disabled={disabled}
              aria-label="Comments"
              color={commentsOverlay.isOpen ? 'primary' : 'default'}
              onClick={() => {
                fieldDetailsOverlay.close()
                commentsOverlay.toggle()
              }}
              onMouseDown={(e) => e.preventDefault()}
              sx={{ borderRadius: 5, px: 1 }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ForumOutlined />
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: '1ch', // To keep it from jumping around when loading
                    opacity: isPending ? 0 : 1,
                    transition: 'opacity 0.2s ease-in-out',
                  }}
                >
                  {commentsCount}
                </Typography>
              </Stack>
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" sx={{ height: 20 }} />
        </>
      )}

      <Tooltip
        title={disabled ? '' : 'Field Details'}
        arrow
        disableInteractive
        enterDelay={1000}
      >
        <IconButton
          size="small"
          disabled={disabled}
          aria-label="Field Details"
          color={fieldDetailsOverlay.isOpen ? 'primary' : 'default'}
          onClick={() => {
            commentsOverlay.close()
            fieldDetailsOverlay.toggle()
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <ReadMore
            sx={{
              transform: `rotate(${fieldDetailsOverlay.isOpen ? 0 : '180deg'})`,
            }}
          />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}
